import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import DateCalendarInput from "app/pages/SmartDP/Search/DateCalendarInput/DateCalendarInput";
import {
	fetchSDPAvailableDepartureDate,
	resetSDPAvailableDepartureDate,
} from "app/pages/SmartDP/smartDPActionCreators";
import { useCallback, useEffect, useRef, useState } from "react";
import isEmpty from "lodash/isEmpty";
import min from "lodash/min";
import max from "lodash/max";
import maxBy from "lodash/maxBy";
import { addDays, format, isWithinInterval } from "date-fns";
import { useField, useFormikContext } from "formik";
import { HTTP_STATUS_CODES, SDP_ROTATION_DATE_FORMAT } from "app/constants";

const MIN_CALENDAR_DATE_OFFSET = 2;
const MAX_CALENDAR_DATE_OFFSET = 340;
const MIN_BOOKING_DATE_OFFSET = 3;
const MAX_BOOKING_DATE_OFFSET = 28;

export const isSdpDateDisabled = ({
	date,
	selectedDepartureDate,
	selectedEndDate,
	durationByAvailableDate = {},
	isCalendarAvailableDateError = false,
	maxSelectableDate,
}) => {
	const hasDurations = !isEmpty(durationByAvailableDate);
	const hasError = isCalendarAvailableDateError;

	if (!date || (!hasDurations && !hasError)) {
		return true;
	}

	if (hasDurations && !hasError) {
		const currentDateKey = format(new Date(date), SDP_ROTATION_DATE_FORMAT);

		if (selectedDepartureDate && !selectedEndDate) {
			const departureKey = format(new Date(selectedDepartureDate), SDP_ROTATION_DATE_FORMAT);
			const availableData = durationByAvailableDate[departureKey] || [];
			const isValidEndDate = availableData.some(item => item.ed === currentDateKey);
			// Posterior departure keys that aren't valid ends would trigger the
			// invalid-end clear effect and make the existing departure jump.
			const isValidAnteriorDepartureDate =
				currentDateKey <= departureKey &&
				Object.keys(durationByAvailableDate).includes(currentDateKey);

			return !(isValidEndDate || isValidAnteriorDepartureDate);
		}

		return !Object.keys(durationByAvailableDate).includes(currentDateKey);
	}

	if (
		!hasDurations &&
		hasError &&
		selectedDepartureDate &&
		!selectedEndDate &&
		maxSelectableDate
	) {
		const dateTime = new Date(date).getTime();
		const minDate = addDays(new Date(selectedDepartureDate), MIN_BOOKING_DATE_OFFSET).getTime();
		const maxDate = maxSelectableDate.getTime();

		return !(dateTime >= minDate && dateTime <= maxDate);
	}

	return false;
};
const SmartDPDateCalendarContainer = props => {
	const {
		uri,
		hasLocationSelection = false,
		fetchSDPAvailableDepartureDate,
		resetSDPAvailableDepartureDate,
		updateFloatingPositionReference = () => {},
		handleDayChange = () => {},
		popperOffset = [],
		durationByAvailableDate = {},
	} = props;
	const [field] = useField(props);

	const selectedDepartureDate = field.value?.departureDate;
	const selectedEndDate = field.value?.endDate;

	const { setFieldValue, setFieldTouched } = useFormikContext();

	// isCalendarAvailableDateError is used to manage the case where we have an error when we fetch available dates for the departure city / destination
	// we want in that case a fully opened calendar instead of displaying an error message
	const [isCalendarAvailableDateError, setIsCalendarAvailableDateError] = useState(false);
	// Prevent stale responses from clearing valid dates.
	const fetchRequestIdRef = useRef(0);

	const today = new Date();

	const sdpDefaultCalendarMinDate = format(
		addDays(today, MIN_CALENDAR_DATE_OFFSET),
		SDP_ROTATION_DATE_FORMAT
	);

	const sdpDefaultCalendarMaxDate = format(
		addDays(today, MAX_CALENDAR_DATE_OFFSET),
		SDP_ROTATION_DATE_FORMAT
	);

	const availableDates = Object.keys(durationByAvailableDate);

	const calendarMinDate =
		availableDates.length > 0 && !isCalendarAvailableDateError
			? min(availableDates)
			: sdpDefaultCalendarMinDate;

	let calendarMaxDate =
		availableDates.length > 0 && !isCalendarAvailableDateError
			? max(availableDates)
			: sdpDefaultCalendarMaxDate;

	const maxSelectableDate = addDays(new Date(selectedDepartureDate), MAX_BOOKING_DATE_OFFSET);

	// gestion de la date de retour sélectionnable
	if (selectedDepartureDate && !isEmpty(durationByAvailableDate)) {
		// Pour afficher le mois de fin du calendrier afin que le user puisse sélectionner
		// la date de fin la plus lointaine possible pour la date de départ sélectionnée
		// Cela permet aussi de ne pas afficher les mois non utiles pour la sélection de la date de retour
		const formattedSelectedDepartureDate = format(
			selectedDepartureDate,
			SDP_ROTATION_DATE_FORMAT
		);

		// update duration from selected departureDate
		const durationsOfSelectedDepartureDate =
			durationByAvailableDate[formattedSelectedDepartureDate];

		if (!selectedEndDate && durationsOfSelectedDepartureDate) {
			// si pas de date de fin sélectionnée et que l'on a des durées pour la date de départ
			// la date max selectionnable est la date max de la durée maximale
			const durationMax = maxBy(
				durationsOfSelectedDepartureDate,
				duration => duration?.duration
			);

			calendarMaxDate = durationMax?.ed;
		} else if (!selectedEndDate && isCalendarAvailableDateError) {
			// si pas de date de fin sélectionnée et que l'on a pas de durée
			// la date max selectionnable est la date de départ + 28 jours
			calendarMaxDate = format(maxSelectableDate, SDP_ROTATION_DATE_FORMAT);
		}
	}

	const isCalendarDisabled = isEmpty(durationByAvailableDate) && !isCalendarAvailableDateError;

	const clearTravelDatesFields = () => {
		setFieldValue("travelDates.departureDate", undefined);
		setFieldValue("travelDates.endDate", undefined);
		setFieldValue("duration", undefined);
		setFieldTouched("travelDates", false);
	};

	// get durations when the value of departureDate change
	// and verify if selectedDepartureDate is available
	useEffect(() => {
		if (selectedDepartureDate && !isEmpty(durationByAvailableDate)) {
			const formattedSelectedDepartureDate = format(
				selectedDepartureDate,
				SDP_ROTATION_DATE_FORMAT
			);

			// update duration from selected departureDate
			const durationsOfSelectedDepartureDate =
				durationByAvailableDate[formattedSelectedDepartureDate];

			handleDayChange({
				durationsOfSelectedDepartureDate,
				selectedEndDate,
				selectedDepartureDate,
			});

			// check is selected departure date is available
			const filteredDurationByAvailableDate = Object.keys(durationByAvailableDate).filter(
				date => {
					return isWithinInterval(new Date(date), {
						start: new Date(calendarMinDate),
						end: new Date(calendarMaxDate),
					});
				}
			);

			// if not, clear departure date, end Date and duration fields
			if (!filteredDurationByAvailableDate.includes(formattedSelectedDepartureDate)) {
				clearTravelDatesFields();
			}

			if (selectedEndDate) {
				const formattedSelectedEndDate =
					selectedEndDate && format(selectedEndDate, SDP_ROTATION_DATE_FORMAT);

				// check if selectedEndDate is available
				const dataOfSelectedDepartureDateMatchingSelectedEndDate = durationsOfSelectedDepartureDate?.find(
					data => {
						return data.ed === formattedSelectedEndDate;
					}
				);
				// if not clear departure date, end Date and duration fields
				if (!dataOfSelectedDepartureDateMatchingSelectedEndDate) {
					clearTravelDatesFields();
				}
			}
		} else if (
			selectedDepartureDate &&
			selectedEndDate &&
			isEmpty(durationByAvailableDate) &&
			isCalendarAvailableDateError
		) {
			handleDayChange({
				durationsOfSelectedDepartureDate: [],
				selectedEndDate,
				selectedDepartureDate,
			});
		} else if (!selectedDepartureDate && !selectedEndDate) {
			const durationsOfSelectedDepartureDate = {};
			handleDayChange({
				durationsOfSelectedDepartureDate,
				selectedEndDate,
				selectedDepartureDate,
			});
		}
	}, [
		durationByAvailableDate,
		selectedDepartureDate,
		selectedEndDate,
		isCalendarAvailableDateError,
	]);

	useEffect(() => {
		if (uri) {
			fetchRequestIdRef.current += 1;
			const requestId = fetchRequestIdRef.current;
			fetchSDPAvailableDepartureDate(uri)
				// eslint-disable-next-line consistent-return
				.then(response => {
					// Outdated response, ignore it.
					if (requestId !== fetchRequestIdRef.current) {
						return null;
					}
					const { status: responseStatus } = response || {};
					if (responseStatus !== HTTP_STATUS_CODES.OK) {
						setIsCalendarAvailableDateError(true);
						return null;
					}
					// No dates available for current location
					if (isEmpty(response?.data)) {
						clearTravelDatesFields();
					}
				})
				.catch(() => {
					// Outdated error, ignore it
					if (requestId !== fetchRequestIdRef.current) {
						return;
					}
					// we have to clear available dates for the departure city / destination
					// to avoid displaying the calendar with the previous available dates for another departure city / destination
					resetSDPAvailableDepartureDate();
					setIsCalendarAvailableDateError(true);
				});
		} else {
			// we have to clear available dates for the departure city / destination
			// to avoid displaying the calendar with the previous available dates for another departure city / destination
			resetSDPAvailableDepartureDate();
			if (hasLocationSelection) {
				clearTravelDatesFields();
			}
		}
	}, [uri, hasLocationSelection, isCalendarAvailableDateError]);

	const checkDateAvailability = useCallback(
		date =>
			isSdpDateDisabled({
				date,
				selectedDepartureDate,
				selectedEndDate,
				durationByAvailableDate,
				isCalendarAvailableDateError,
				maxSelectableDate,
			}),
		[
			durationByAvailableDate,
			selectedDepartureDate,
			selectedEndDate,
			isCalendarAvailableDateError,
			maxSelectableDate,
		]
	);

	return (
		<DateCalendarInput
			{...props}
			departureDateMin={calendarMinDate}
			departureDateMax={calendarMaxDate}
			updateFloatingPositionReference={updateFloatingPositionReference}
			checkDateAvailability={checkDateAvailability}
			handleDayChange={handleDayChange}
			isCalendarDisabled={isCalendarDisabled}
			popperOffset={popperOffset}
		/>
	);
};

SmartDPDateCalendarContainer.propTypes = {
	uri: PropTypes.string,
	updateFloatingPositionReference: PropTypes.func,
	fetchSDPAvailableDepartureDate: PropTypes.func,
	resetSDPAvailableDepartureDate: PropTypes.func,
	handleDayChange: PropTypes.func,
	popperOffset: PropTypes.array,
	durationByAvailableDate: PropTypes.object,
	hasLocationSelection: PropTypes.bool,
};

const mapStateToProps = state => ({
	durationByAvailableDate: state.smartDP.availabilitiesDate,
});

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchSDPAvailableDepartureDate,
			resetSDPAvailableDepartureDate,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(SmartDPDateCalendarContainer);
